import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { Loader } from "../Common/Loader";
import useVendorStore from "../HomePage/useVendorStore";
import GenerateUpcModal from "./GenerateUpcModal";
import { STATUS_BEGIN, STATUS_FAIL } from "utils/constants/others";
import ShowToast from "../Common/Toast";
import styles from "./DownloadLabels.module.scss";

const API_PAGE_SIZE = 1000;
const PAGE_SIZE = 20;

const DownloadLabels = () => {
  const navigate = useNavigate();
  const outletId = window.localStorage.getItem("outlet_id");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const debounceTimer = useRef(null);

  const getVendorProductsFromPO = useVendorStore((s) => s.getVendorProductsFromPO);
  const getVendorProducts = useVendorStore((s) => s.getVendorProducts);
  const vendorProductsFromPO = useVendorStore((s) => s.vendorProductsFromPO);
  const vendorProducts = useVendorStore((s) => s.vendorProducts);
  const vendorProductsTotalCount = useVendorStore((s) => s.vendorProductsTotalCount);
  const vendorProductsFromPOStatus = useVendorStore((s) => s.vendorProductsFromPOStatus);
  const vendorProductsStatus = useVendorStore((s) => s.vendorProductsStatus);

  useEffect(() => {
    getVendorProductsFromPO({ vendor_id: outletId });
  }, []);

  const isSearching = !!searchQuery.trim();

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!query.trim()) return;
    debounceTimer.current = setTimeout(() => {
      getVendorProducts({
        vendor_id: outletId,
        search: query.trim(),
        page_size: API_PAGE_SIZE,
      });
    }, 400);
  }, [outletId]);

  const isLoading =
    (isSearching ? vendorProductsStatus : vendorProductsFromPOStatus) === STATUS_BEGIN;
  const hasError =
    (isSearching ? vendorProductsStatus : vendorProductsFromPOStatus) === STATUS_FAIL;

  const allProducts = isSearching ? vendorProducts : (vendorProductsFromPO || []);
  const totalPages = Math.ceil((allProducts.length || 0) / PAGE_SIZE);
  const products = allProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleGenerateQR = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const formatPrice = (price) => {
    if (price == null) return "-";
    return `₹${Number(price).toLocaleString("en-IN")}`;
  };

  return (
    <React.Fragment>
      <Header showLogout={false} showProfile={true} />
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5m0 0l6 6m-6-6l6-6" stroke="#1c1c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className={styles.pageTitle}>Download packaging labels</h1>
        </div>

        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchQuery && (
            <span className={styles.clearBtn} onClick={() => { setSearchQuery(""); setCurrentPage(1); }}>
              ✕
            </span>
          )}
        </div>

        {isLoading ? (
          <Loader />
        ) : products?.length > 0 ? (
          <>
            <div className={styles.list}>
              {products.map((product) => (
                <div key={product.product_number} className={styles.card}>
                  <div className={styles.cardLeft}>
                    <div className={styles.cardImageWrapper}>
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.product_name}
                          className={styles.cardImage}
                        />
                      ) : (
                        <div className={styles.cardImagePlaceholder}>
                          <span>No Image</span>
                        </div>
                      )}
                    </div>
                    <div className={styles.cardInfo}>
                      <span className={styles.productName}>{product.product_name}</span>
                      <span className={styles.productNumber}>
                        #{product.product_number}
                        {product.price != null && (
                          <span className={styles.productPrice}>
                            {" · "}{formatPrice(product.price)}/{product.uom || "unit"}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className={styles.cardMeta}>
                    {product.quantity != null && (
                      <div className={styles.metaBlock}>
                        <span className={styles.metaValue}>{product.quantity} {product.uom || "Count"}</span>
                        <span className={styles.metaLabel}>Order qty.</span>
                      </div>
                    )}
                    {product.amount != null && (
                      <div className={styles.metaBlock}>
                        <span className={styles.metaValue}>{formatPrice(product.amount)}</span>
                        <span className={styles.metaLabel}>Order amount</span>
                      </div>
                    )}
                  </div>

                  <button
                    className={styles.generateBtn}
                    onClick={() => handleGenerateQR(product)}
                  >
                    Generate QR
                  </button>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ← Prev
                </button>
                <span className={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <span>No products found</span>
          </div>
        )}
      </div>
      <Footer />

      <GenerateUpcModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        item={selectedProduct}
      />

      {hasError && (
        <ShowToast
          errorMsg="Failed to fetch products. Please try again."
          type="error"
        />
      )}
    </React.Fragment>
  );
};

export default DownloadLabels;
